
import React, { useEffect, useRef } from "react";
import BackgroundImage from "./BackgroundImage";

const OurTracks = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll(".fade-in-element");
            elements.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add("animate-fade-in");
              }, index * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const tracks = [
    {
      title: "Autonomous AI Agents for Work & Life",
      description: "Build AI teammates that think, act, and get things done.",
      icon: "🤖",
      backgroundImage: "url('/lovable-uploads/451e8b9b-19c7-4715-8958-6b9c1a4c40fd.png')",
      gradientOverlay: "from-orange-500/20 via-red-500/30 to-pink-600/40"
    },
    {
      title: "Generative AI for Real-World Creation",
      description: "Go beyond chat: generate videos, pitches, stories, music, websites, or 3D assets.",
      icon: "🎨",
      backgroundImage: "url('/lovable-uploads/566a49a2-5553-4a8f-9fb1-2bd20c7ab011.png')",
      gradientOverlay: "from-blue-500/20 via-purple-500/30 to-indigo-600/40"
    },
    {
      title: "Personal Intelligence & Decision Support",
      description: "Give people better decisions, not just better answers.",
      icon: "🧠",
      backgroundImage: "url('/lovable-uploads/4c8d0304-78fa-4988-85a8-28e255469c97.png')",
      gradientOverlay: "from-emerald-500/20 via-teal-500/30 to-cyan-600/40"
    },
    {
      title: "AI for the Next Billion Users",
      description: "Make AI radically useful for underserved markets, devices, and languages.",
      icon: "🌍",
      backgroundImage: "url('/lovable-uploads/451e8b9b-19c7-4715-8958-6b9c1a4c40fd.png')",
      gradientOverlay: "from-orange-500/20 via-red-500/30 to-pink-600/40"
    }
  ];

  return (
    <section id="tracks" style={{ backgroundColor: 'var(--theme-bg)' }} className="w-full m-0 p-0 py-12 sm:py-16 md:py-20" ref={sectionRef}>
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto" style={{ backgroundColor: 'var(--theme-bg)' }}>
        {/* Centered header with badge */}
        <div className="flex justify-center mb-10 sm:mb-16">
          <div className="inline-flex items-center justify-center px-4 py-2 rounded-full opacity-0 fade-in-element" style={{
            backgroundColor: 'rgba(242, 76, 39, 0.1)',
            border: '1px solid rgba(242, 76, 39, 0.2)',
            color: 'rgba(242, 76, 39, 1)'
          }}>
            <span style={{
              backgroundColor: 'rgba(242, 76, 39, 1)'
            }} className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2 text-sm">4</span>
            <span>Our Tracks</span>
          </div>
        </div>
        
        {/* Section heading */}
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold dark:gradient-heading-dark pb-2 opacity-0 fade-in-element" style={{
            background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent'
          }}>
            <span className="hidden md:inline">Four Innovation Tracks</span>
            <span className="md:hidden">4 Innovation Tracks</span>
          </h2>
          <p className="text-lg mt-4 text-gray-600 dark:text-gray-300 opacity-0 fade-in-element">
            Choose your focus area and build the future of AI
          </p>
        </div>
        
        {/* Four tracks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8 sm:mb-16">
          {tracks.map((track, index) => (
            <div key={track.title} className="relative overflow-hidden p-6 rounded-2xl transition-all duration-300 hover:translate-y-[-5px] hover:shadow-xl opacity-0 fade-in-element" style={{
              backdropFilter: 'blur(8px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              animationDelay: `${0.1 * index}s`
            }}>
              {/* Background image */}
              <BackgroundImage 
                src={track.backgroundImage.replace("url('", "").replace("')", "")}
                className="absolute inset-0 z-0"
                style={{
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
              
              {/* Gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${track.gradientOverlay} z-5`}></div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl mb-4" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}>
                  {track.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-white">
                  {track.title}
                </h3>
                <p className="text-sm sm:text-base text-white">
                  {track.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTracks;
